import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';

import { useSelector } from 'react-redux';
import { useAuthUser } from 'store/auth';

import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Card, CardContent, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Container } from 'Components/Container';
import Paper from '@mui/material/Paper';

import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  editPolicyVersionStatus,
  getPolicyById,
  getTotalPoliciesAcceptsByPolicyIdAndPolicyVersionId,
  sendPolicyUserAcceptRequest,
  getPolicyComments,
  addPolicyComment,
  rejectPolicyVersionWithEmail,
  regenerateAttachmentDownloadUrl,
} from 'services/policies-service';
import { backendService } from 'services';
import { formatDateTimeToDateTimeBR, formatDateToDateBRPolish } from 'utils/date_utils';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { ButtonForm } from 'Components/Form/styles';
import 'react-quill/dist/quill.snow.css';
import { Column, Row } from 'Components/Grid';
import { useReactToPrint } from 'react-to-print';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

// Configurar o worker do PDF.js com a versão local
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export const PolicyVersionShow = () => {
  const { id, idVersion } = useParams();
  const user = useSelector(useAuthUser);
  const componentRef = useRef();
  const combinedRef = useRef();
  const navigate = useNavigate();

  const SwalReact = withReactContent(Swal);
  const [refetch, setRefetch] = useState(0);
  
  // Estados para controlar o conteúdo combinado
  const [showCombinedContent, setShowCombinedContent] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  
  // Estados para a funcionalidade de transcrição
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');

  // Estados para comentários
  const [comments, setComments] = useState([]);
  
  // Estado para armazenar a URL atual do anexo (compartilhado com AttachmentViewer)
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState(null);

  const {
    isLoading,
    error,
    data: policy,
  } = useQuery(['getPolicyById', refetch], async () => {
    const resPolicy = await getPolicyById(id);

    if (resPolicy.status === 200) return resPolicy.data;

    return null;
  });

  const { data: policyAccepts } = useQuery(['getTotalPolicyAccepts', refetch, id, idVersion], async () => {
    const resTotalPolicyAccepts = await getTotalPoliciesAcceptsByPolicyIdAndPolicyVersionId(id, idVersion);

    if (resTotalPolicyAccepts.status === 200) return resTotalPolicyAccepts.data;

    return { total: 0 };
  });

  // Simular comentários de exemplo (até o backend ser implementado)
  const loadExampleComments = () => {
    const exampleComments = [
      {
        id: 1,
        content: "Esta política precisa de ajustes na seção de segurança",
        type: "comment",
        authorName: "João Silva",
        authorRole: "Revisor",
        createdAt: "2025-01-15T10:30:00.000Z"
      },
      {
        id: 2,
        content: "Documento rejeitado por não atender aos critérios de compliance",
        type: "rejection",
        authorName: "Maria Santos",
        authorRole: "Aprovador",
        createdAt: "2025-01-14T14:20:00.000Z"
      },
      {
        id: 3,
        content: "Excelente trabalho na documentação dos processos",
        type: "comment",
        authorName: "Pedro Costa",
        authorRole: "Usuário",
        createdAt: "2025-01-13T09:15:00.000Z"
      }
    ];
    setComments(exampleComments);
  };

  // Carregar comentários quando a política for carregada
  useQuery(['getPolicyComments', id, idVersion, refetch], async () => {
    try {
      const response = await getPolicyComments(id, idVersion);
      if (response.status === 200) {
        setComments(response.data || []);
        return response.data;
      }
    } catch (error) {
      console.log('Erro ao carregar comentários, usando dados de exemplo:', error);
      // Se houver erro na API, carregar comentários de exemplo
      loadExampleComments();
    }
    return [];
  });

  const mutation = useMutation(
    () => {
      return sendPolicyUserAcceptRequest(id, idVersion);
    },
    {
      onError: error => {
        // An error happened!
        SwalReact.fire({
          title: 'Erro!',
          text: error.response.data.message ?? error.message,
          icon: 'error',
        });
      },
      onSuccess: data => {
        if (data.status === 200) {
          setRefetch(refetch + 1);
        }
      },
    },
  );

  const getClassificationPolicy = policy => {
    return policy ? policy?.classification : '';
  };

  const getVersionPolicy = (policy, idVersion) => {
    return policy ? policy?.policyVersions[idVersion]?.version : '';
  };

  // Função para verificar se o usuário atual pode adicionar comentários
  const canAddComment = () => {
    if (!policy || !policy.policyVersions || !policy.policyVersions[parseInt(idVersion)] || !user) {
      return false;
    }

    const currentVersion = policy.policyVersions[parseInt(idVersion)];
    const currentUserId = user.userId;

    // Verificar se é autor
    if (currentVersion.author && (currentVersion.author._id === currentUserId || currentVersion.author.id === currentUserId)) {
      return true;
    }

    // Verificar se é revisor
    if (currentVersion.reviewer && (currentVersion.reviewer._id === currentUserId || currentVersion.reviewer.id === currentUserId)) {
      return true;
    }

    // Verificar se é aprovador
    if (currentVersion.approver && (currentVersion.approver._id === currentUserId || currentVersion.approver.id === currentUserId)) {
      return true;
    }

    // Verificar se é publicador
    if (currentVersion.publisher && (currentVersion.publisher._id === currentUserId || currentVersion.publisher.id === currentUserId)) {
      return true;
    }

    return false;
  };

  // Função para verificar se o usuário atual pode visualizar o histórico de aprovação
  const canViewApprovalHistory = () => {
    if (!policy || !policy.policyVersions || !policy.policyVersions[parseInt(idVersion)] || !user) {
      return false;
    }

    const currentVersion = policy.policyVersions[parseInt(idVersion)];
    const currentUserId = user.userId;

    // Verificar se é autor
    if (currentVersion.author && (currentVersion.author._id === currentUserId || currentVersion.author.id === currentUserId)) {
      return true;
    }

    // Verificar se é revisor
    if (currentVersion.reviewer && (currentVersion.reviewer._id === currentUserId || currentVersion.reviewer.id === currentUserId)) {
      return true;
    }

    // Verificar se é aprovador
    if (currentVersion.approver && (currentVersion.approver._id === currentUserId || currentVersion.approver.id === currentUserId)) {
      return true;
    }

    return false;
  };

  // Função para verificar se o usuário atual pode exportar PDF
  const canExportPDF = () => {
    if (!policy || !policy.policyVersions || !policy.policyVersions[parseInt(idVersion)] || !user) {
      return false;
    }

    const currentVersion = policy.policyVersions[parseInt(idVersion)];
    const currentUserId = user.userId;

    // Verificar se é revisor
    if (currentVersion.reviewer && (currentVersion.reviewer._id === currentUserId || currentVersion.reviewer.id === currentUserId)) {
      return true;
    }

    // Verificar se é aprovador
    if (currentVersion.approver && (currentVersion.approver._id === currentUserId || currentVersion.approver.id === currentUserId)) {
      return true;
    }

    // Verificar se é publicador
    if (currentVersion.publisher && (currentVersion.publisher._id === currentUserId || currentVersion.publisher.id === currentUserId)) {
      return true;
    }

    if (user.roleActive !== 'user_default') {
      return true;
    }

    return false;
  };

  const updateStatus = async status => {
    try {
      SwalReact.showLoading();
      await editPolicyVersionStatus(id, idVersion, status);
      SwalReact.hideLoading();
      SwalReact.fire({
        title: 'Sucesso!',
        text: 'Status alterado com sucesso!',
        icon: 'success',
        willClose: () => {
          setRefetch(refetch + 1);
        },
      });
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message,
        icon: 'error',
      });
    }
  };

  const handleReject = async (tipo) => {
    // Mostrar modal para obter motivo da recusa
    const getTitleByType = (tipo) => {
      if (tipo === 'revisor') return 'Revisão';
      if (tipo === 'aprovador') return 'Aprovação';
      if (tipo === 'publicador') return 'Publicação';
      return 'Documento';
    };
    
    const { value: motivo } = await SwalReact.fire({
      title: `Recusar ${getTitleByType(tipo)}`,
      html: `
        <div style="text-align: left; margin-bottom: 15px;">
          <p>Você está prestes a recusar este documento.</p>
          <p><strong>Por favor, informe o motivo da recusa:</strong></p>
        </div>
      `,
      input: 'textarea',
      inputPlaceholder: 'Digite o motivo da recusa...',
      inputAttributes: {
        'aria-label': 'Motivo da recusa',
        style: 'min-height: 100px; resize: vertical;'
      },
      showCancelButton: true,
      confirmButtonText: 'Recusar Documento',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#666',
      inputValidator: (value) => {
        if (!value || value.trim().length < 10) {
          return 'O motivo deve ter pelo menos 10 caracteres';
        }
      },
      customClass: {
        popup: 'reject-modal',
        confirmButton: 'reject-confirm-btn',
        input: 'reject-textarea'
      },
      didOpen: () => {
        // Adicionar estilos customizados para o modal
        const style = document.createElement('style');
        style.textContent = `
          .reject-modal {
            font-family: 'Source Sans Pro', sans-serif;
          }
          .reject-confirm-btn {
            background-color: #d32f2f !important;
            border-color: #d32f2f !important;
          }
          .reject-confirm-btn:hover {
            background-color: #b71c1c !important;
            border-color: #b71c1c !important;
          }
          .reject-textarea {
            font-family: 'Source Sans Pro', sans-serif;
            border: 2px solid #ddd !important;
            border-radius: 4px !important;
          }
          .reject-textarea:focus {
            border-color: #d32f2f !important;
            outline: none !important;
          }
        `;
        document.head.appendChild(style);
      }
    });

    if (motivo) {
      try {
        SwalReact.showLoading();
        
        let regressionStatus;
        if (tipo === 'revisor') {
          regressionStatus = 'Iniciado';
        } else if (tipo === 'aprovador') {
          regressionStatus = 'Iniciado';
        } else if (tipo === 'publicador') {
          regressionStatus = 'Aprovado';
        }
        
        // Preparar dados da rejeição
        const rejectionData = {
          policyId: id,
          policyVersionId: idVersion,
          content: `Documento rejeitado: ${motivo.trim()}`,
          motivo: motivo.trim(),
          type: 'rejection',
          authorId: user.userId,
          authorName: user.name || user.email || 'Usuário',
          authorRole: tipo === 'revisor' ? 'Revisor' : tipo === 'aprovador' ? 'Aprovador' : 'Publicador',
          createdAt: new Date().toISOString()
        };
        
        try {
          // Usar o novo endpoint que envia email automaticamente
          const rejectionPayload = {
            status: regressionStatus,
            rejectionReason: motivo.trim(),
            rejectedBy: user.name || user.email || 'Usuário',
            rejectedByType: tipo
          };
          
          await rejectPolicyVersionWithEmail(id, idVersion, rejectionPayload);
          
          // Adicionar rejeição à lista local de comentários para exibição imediata
          const rejectionComment = {
            id: Date.now(), // ID temporário
            ...rejectionData
          };
          setComments(prevComments => [rejectionComment, ...prevComments]);
          
        } catch (error) {
          console.error('Erro ao processar rejeição:', error);
          // Fallback para o método antigo se o novo falhar
          try {
            await editPolicyVersionStatus(id, idVersion, regressionStatus);
            await addPolicyComment(id, idVersion, rejectionData);
          } catch (fallbackError) {
            console.error('Erro no fallback:', fallbackError);
          }
          throw error;
        }
        
        SwalReact.hideLoading();
        SwalReact.fire({
          title: 'Documento Recusado!',
          html: `
            <div style="text-align: left;">
              <p>O documento foi recusado com sucesso.</p>
              <p><strong>Motivo:</strong> ${motivo.trim()}</p>
              <p><em>Um email foi enviado para o autor informando sobre a recusa.</em></p>
            </div>
          `,
          icon: 'info',
          confirmButtonText: 'OK',
          willClose: () => {
            setRefetch(refetch + 1);
            // Recarregar comentários
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          },
        });
      } catch (error) {
        SwalReact.hideLoading();
        SwalReact.fire({
          title: 'Erro!',
          text: error?.response?.data?.message || 'Erro ao recusar documento',
          icon: 'error',
        });
      }
    }
  };

  const handleAddComment = async () => {
    // Modal para adicionar comentário geral
    const { value: comentario } = await SwalReact.fire({
      title: 'Adicionar Comentário',
      html: `
        <div style="text-align: left; margin-bottom: 15px;">
          <p>Adicione um comentário sobre esta política:</p>
        </div>
      `,
      input: 'textarea',
      inputPlaceholder: 'Digite seu comentário...',
      inputAttributes: {
        'aria-label': 'Comentário',
        style: 'min-height: 120px; resize: vertical;'
      },
      showCancelButton: true,
      confirmButtonText: 'Adicionar Comentário',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#666',
      inputValidator: (value) => {
        if (!value || value.trim().length < 5) {
          return 'O comentário deve ter pelo menos 5 caracteres';
        }
      },
      customClass: {
        popup: 'comment-modal',
        confirmButton: 'comment-confirm-btn',
        input: 'comment-textarea'
      },
      didOpen: () => {
        // Adicionar estilos customizados para o modal
        const style = document.createElement('style');
        style.textContent = `
          .comment-modal {
            font-family: 'Source Sans Pro', sans-serif;
          }
          .comment-confirm-btn {
            background-color: #1976d2 !important;
            border-color: #1976d2 !important;
          }
          .comment-confirm-btn:hover {
            background-color: #1565c0 !important;
            border-color: #1565c0 !important;
          }
          .comment-textarea {
            font-family: 'Source Sans Pro', sans-serif;
            border: 2px solid #ddd !important;
            border-radius: 4px !important;
          }
          .comment-textarea:focus {
            border-color: #1976d2 !important;
            outline: none !important;
          }
        `;
        document.head.appendChild(style);
      }
    });

    if (comentario) {
      try {
        SwalReact.showLoading();
        
        // Determinar o papel do usuário na política
        let userRole = 'Usuário';
        if (policy && policy.policyVersions && policy.policyVersions[parseInt(idVersion)]) {
          const currentVersion = policy.policyVersions[parseInt(idVersion)];
          const userId = user.userId;
          
          if (currentVersion.author && (currentVersion.author._id === userId || currentVersion.author.id === userId)) {
            userRole = 'Autor';
          } else if (currentVersion.reviewer && (currentVersion.reviewer._id === userId || currentVersion.reviewer.id === userId)) {
            userRole = 'Revisor';
          } else if (currentVersion.approver && (currentVersion.approver._id === userId || currentVersion.approver.id === userId)) {
            userRole = 'Aprovador';
          }
        }
        
        // Preparar dados do comentário
        const commentData = {
          policyId: id,
          policyVersionId: idVersion,
          content: comentario.trim(),
          type: 'comment',
          authorId: user.userId,
          authorName: user.name || user.email || 'Usuário',
          authorRole: userRole,
          createdAt: new Date().toISOString()
        };
        
        try {
          // Tentar salvar no backend primeiro
          await addPolicyComment(id, idVersion, commentData);
          
          // Se sucesso, adicionar à lista local
          const newComment = {
            id: Date.now(), // ID temporário até recarregar
            ...commentData
          };
          setComments(prevComments => [newComment, ...prevComments]);
          
        } catch (apiError) {
          console.log('Erro na API de comentários, usando fallback:', apiError);
          
          // Fallback: usar API de status existente
          await editPolicyVersionStatus(id, idVersion, 'Comentario');
          
          // Adicionar comentário à lista local para exibição imediata
          const newComment = {
            id: Date.now(), // ID temporário
            ...commentData
          };
          setComments(prevComments => [newComment, ...prevComments]);
        }
        
        SwalReact.hideLoading();
        SwalReact.fire({
          title: 'Comentário Adicionado!',
          text: 'Seu comentário foi adicionado com sucesso.',
          icon: 'success',
          confirmButtonText: 'OK',
          willClose: () => {
            setRefetch(refetch + 1);
          },
        });
      } catch (error) {
        SwalReact.hideLoading();
        SwalReact.fire({
          title: 'Erro!',
          text: error?.response?.data?.message || 'Erro ao adicionar comentário',
          icon: 'error',
        });
      }
    }
  };

  const getStatusReview = policyVersion => {
    if (policyVersion.status === 'Publicado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;Publicado
        </div>
      );
    } else if (policyVersion.reviewer?._id !== user.userId) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;Você não é o revisor
        </div>
      );
    } else if (policyVersion.status === 'Iniciado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;Aguardando Conclusão
        </div>
      );
    } else if (policyVersion.status === 'Concluído') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CheckBoxOutlineBlankOutlinedIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#0039C5' }}
              onClick={() => updateStatus('Revisado')}
              title="Revisar política"
            />
            <span style={{ fontSize: '14px' }}>Aprovar</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CloseIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#d32f2f' }}
              onClick={() => handleReject('revisor')}
              title="Recusar revisão"
            />
            <span style={{ fontSize: '14px' }}>Recusar</span>
          </div>
        </div>
      )
    } else {
      return (
         <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;Revisado
        </div>
      );
    }
  };

  const getStatusApprove = policyVersion => {
    if (policyVersion.status === 'Publicado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;Publicado
        </div>
      );
    } else if (user.userId !== policyVersion.approver?._id) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;Você não é o aprovador
        </div>
      );
    } else if (policyVersion.status === 'Iniciado' || policyVersion.status === 'Concluído') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;Aguardando Revisão
        </div>
      );
    } else if (policyVersion.status === 'Revisado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CheckBoxOutlineBlankOutlinedIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#0039C5' }}
              onClick={() => updateStatus('Aprovado')}
              title="Aprovar política"
            />
            <span style={{ fontSize: '14px' }}>Aprovar</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CloseIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#d32f2f' }}
              onClick={() => handleReject('aprovador')}
              title="Recusar revisão"
            />
            <span style={{ fontSize: '14px' }}>Recusar</span>
          </div>
        </div>
      )
    } else {
      return (
         <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;Aprovado
        </div>
      );
    }
  };

  const getStatusPublish = policyVersion => {
    if (policyVersion.status === 'Publicado' ) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
          &nbsp;Publicado
        </div>
      );
    } else if (user.userId !== policyVersion.author?._id) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;Você não é o publicador
        </div>
      );
    } else if (policyVersion.status === 'Aprovado') {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CheckBoxOutlineBlankOutlinedIcon
              sx={{ '&:hover': { cursor: 'pointer' }, fill: '#0039C5' }}
              onClick={() => updateStatus('Publicado')}
              title="Publicar"
            />
            <span style={{ fontSize: '14px' }}>Publicar</span>
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <IndeterminateCheckBoxOutlinedIcon sx={{ '&:hover': { cursor: 'not-allowed' }, fill: 'red' }} />
          &nbsp;Aguardando Aprovação
        </div>
      );
    }
  };

  // Função para imprimir com conteúdo do PDF anexado
  const handlePrintWithAttachment = async () => {
    const policyVersion = policy?.policyVersions[idVersion];
    
    if (policyVersion?.attachment && policyVersion.attachment.mimetype === 'application/pdf') {
      try {
        // Mostrar loading
        SwalReact.fire({
          title: 'Preparando PDF...',
          text: 'Extraindo conteúdo do documento anexado...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => {
            SwalReact.showLoading();
          }
        });

        // Usar a URL atual ou a original do anexo
        const urlToUse = currentAttachmentUrl || policyVersion.attachment.url;
        const fullUrl = urlToUse.startsWith('http') 
          ? urlToUse 
          : `${backendService.defaults.baseURL}${urlToUse}`;
        
        // Carregar e renderizar todas as páginas do PDF
        const response = await fetch(fullUrl);
        const arrayBuffer = await response.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        
        const PRINT_DPI = 150;                // qualidade melhor no papel
        const SCALE = PRINT_DPI / 72;         // 72dpi base do PDF
        const pages = [];
        
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: SCALE });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          
          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;
          
          pages.push(canvas.toDataURL('image/png'));
        }
        
        SwalReact.close();
        setPdfPages(pages);
        setShowCombinedContent(true);
        
        // imprime somente após o React pintar o combinedRef
        setTimeout(() => {
          printCombined();
          // limpeza leve após a impressão
          setTimeout(() => {
            setShowCombinedContent(false);
            setPdfPages([]);
          }, 1000);
        }, 300);
        
      } catch (e) {
        SwalReact.close();
        console.error('Erro ao processar PDF para impressão:', e);
        handlePrint(); // fallback
      }
    } else {
      // Se não houver anexo PDF, usar a impressão normal
      handlePrint();
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
      @page { size: 10in 12in }
      @media print {
        .no-print { display: none !important; }
      }
    `,
  });

  const printCombined = useReactToPrint({
    content: () => combinedRef.current,
    documentTitle: `${policy?.name} - v${policy?.policyVersions?.[idVersion]?.version || ''}`,
    pageStyle: `
      @page { size: A4; margin: 12mm; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; }
        /* Evita quebras feias em imagens e tabelas */
        img, table, tr, td, th, .ql-editor, .no-break {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .page-break { page-break-before: always; break-before: page; }
        /* Tabelas mais legíveis no papel */
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ddd; padding: 8px; }
        /* Conteúdo Quill com tipografia consistente no print */
        .ql-editor { font-size: 12pt; line-height: 1.5; }
        /* Imagens do PDF renderizado ocupam a largura da página */
        .pdf-page-img { width: 100%; height: auto; }
        /* Ocultar comentários na impressão */
        .no-print { display: none !important; }
      }
    `,
  });

  // Função para salvar o texto transcrito
  const handleSaveTranscription = () => {
    // Aqui você pode implementar a lógica para salvar o texto editado
    // Por exemplo, criar uma nova versão da política com o texto transcrito
    SwalReact.fire({
      title: 'Sucesso!',
      text: 'Texto transcrito salvo com sucesso!',
      icon: 'success',
      confirmButtonText: 'OK'
    }).then(() => {
      setShowTranscriptionModal(false);
      setTranscribedText('');
    });
  };

  // Função para determinar o ícone do arquivo baseado no tipo
  const getFileIcon = (mimetype) => {
    if (mimetype === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 24 }} />;
    } else if (mimetype === 'application/msword' || mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return <DescriptionIcon sx={{ color: '#1976d2', fontSize: 24 }} />;
    }
    return <AttachFileIcon sx={{ color: '#666', fontSize: 24 }} />;
  };

  // Função para formatar o tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Componente para exibir o anexo
  const AttachmentViewer = React.memo(({ attachment }) => {
    const [attachmentUrl, setAttachmentUrl] = useState(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const iframeRef = React.useRef(null);

    // Inicializar com URL original
    React.useEffect(() => {
      const init = async () => {
        if (!attachment) return;

        console.log("🚀 Inicializando AttachmentViewer, pedindo URL sob demanda...");
        try {
          const response = await regenerateAttachmentDownloadUrl(id, idVersion);
          if (response.data?.url) {
            console.log("✅ URL fresca recebida:", response.data.url);
            setAttachmentUrl(response.data.url);
            setCurrentAttachmentUrl(response.data.url);
          } else {
            console.error("❌ Backend não retornou URL válida");
          }
        } catch (err) {
          console.error("❌ Erro ao gerar URL inicial:", err);
          // fallback para a que veio no objeto (se existir)
          setAttachmentUrl(attachment.url);
          setCurrentAttachmentUrl(attachment.url);
        }
      };

      init();
    }, [attachment]);

    // Monitorar mudanças na URL do attachment
    React.useEffect(() => {
      if (attachmentUrl) {
        console.log('🔄 URL do attachment alterada, iframe será re-renderizado:', attachmentUrl);
        console.log('🎯 Key do iframe:', attachmentUrl);
      }
    }, [attachmentUrl]);

    if (!attachment) return null;

    // Função para regenerar URL automaticamente (chamada pelo onError do iframe)
    const regenerateUrlAutomatically = async () => {
      try {
        setIsRegenerating(true);
        console.log('🔄 Iniciando regeneração automática da URL...');
        console.log('📋 Parâmetros:', { policyId: id, versionId: idVersion });
        console.log('🔗 URL atual:', attachmentUrl);
        
        const response = await regenerateAttachmentDownloadUrl(id, idVersion);
        console.log('📡 Resposta da API:', response);
        
        if (response.data?.url) {
          console.log('✅ URL regenerada com sucesso:', response.data.url);
          console.log('🔄 Atualizando estados...');
          setAttachmentUrl(response.data.url);
          setCurrentAttachmentUrl(response.data.url); // Manter compatibilidade
          setIframeError(false);
          console.log('✅ Estados atualizados, iframe deve recarregar automaticamente');
        } else {
          console.error('❌ Resposta da API não contém URL válida:', response.data);
          setIframeError(true);
        }
      } catch (error) {
        console.error('❌ Erro ao regenerar URL automaticamente:', error);
        console.error('📋 Detalhes do erro:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        setIframeError(true);
      } finally {
        setIsRegenerating(false);
        console.log('🏁 Regeneração automática finalizada');
      }
    };

    // Função para regenerar URL manualmente
    const regenerateUrl = async () => {
      try {
        setIsRegenerating(true);
        const response = await regenerateAttachmentDownloadUrl(id, idVersion);
        if (response.data?.url) {
          setAttachmentUrl(response.data.url);
          setCurrentAttachmentUrl(response.data.url); // Manter compatibilidade
          setIframeError(false);
          SwalReact.fire({
            title: 'Sucesso!',
            text: response.data.message || 'URL do anexo foi regenerada com sucesso.',
            icon: 'success',
          });
        }
      } catch (error) {
        console.error('Erro ao regenerar URL:', error);
        SwalReact.fire({
          title: 'Erro!',
          text: 'Não foi possível regenerar o link do documento. Tente novamente.',
          icon: 'error',
        });
      } finally {
        setIsRegenerating(false);
      }
    };

    // Função chamada quando o iframe carrega
    const handleIframeLoad = () => {
      console.log('Iframe carregado com sucesso para URL:', attachmentUrl);
      setIframeError(false);
      
      // Verificar se o conteúdo do iframe indica erro de acesso
      setTimeout(() => {
        try {
          const iframe = iframeRef.current;
          if (iframe && iframe.contentDocument) {
            const content = iframe.contentDocument.body?.innerText || '';
            console.log('Conteúdo do iframe:', content.substring(0, 200));
            
            // Verificar se contém mensagens de erro de acesso
            if (content.includes('Access Denied') || 
                content.includes('AccessDenied') || 
                content.includes('Forbidden') ||
                content.includes('403') ||
                content.includes('Token has expired') ||
                content.includes('SignatureDoesNotMatch') ||
                content.includes('ExpiredToken') ||
                content.includes('The provided token has expired')) {
              console.log('Erro de acesso detectado no conteúdo do iframe → regenerando URL');
              setIframeError(true);
              regenerateUrlAutomatically();
            }
          }
        } catch (error) {
          // CORS pode impedir acesso ao conteúdo, isso é normal
          console.log('Não foi possível verificar conteúdo do iframe (CORS):', error.message);
        }
      }, 1000); // Aguardar 1 segundo para o conteúdo carregar
    };

    // Função chamada quando o iframe falha ao carregar
    const handleIframeError = (event) => {
      console.log('Erro no iframe detectado:', event);
      console.log('URL que falhou:', attachmentUrl);
      setIframeError(true);
      regenerateUrlAutomatically();
    };

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon />
            Arquivo Anexado
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {getFileIcon(attachment.mimetype)}
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {attachment.originalName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {attachment.mimetype} • {formatFileSize(attachment.size)}
              </Typography>
            </Box>
          </Box>

          {/* Visualizador de PDF normal */}
          {attachment.mimetype === 'application/pdf' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Visualização do PDF:
              </Typography>
              
              {/* Botão para regenerar URL se houver erro */}
              {iframeError && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    O link do documento expirou. Clique no botão abaixo para recarregar.
                  </Typography>
                  <ButtonForm
                    secondary
                    size="small"
                    label={isRegenerating ? 'Recarregando...' : 'Recarregar Documento'}
                    onClick={regenerateUrl}
                    disabled={isRegenerating}
                  />
                </Box>
              )}
              
              <Box 
                sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: '600px',
                  width: '100%'
                }}
              >
                <iframe
                  ref={iframeRef}
                  key={attachmentUrl} // Força re-render quando URL muda
                  src={attachmentUrl || 'about:blank'}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title={attachment.originalName}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                >
                  <p>
                    Seu navegador não suporta iframes. 
                    <a href={attachmentUrl || '#'} target="_blank" rel="noopener noreferrer">
                      Clique aqui para visualizar o PDF
                    </a>
                  </p>
                </iframe>
              </Box>
            </Box>
          )}

          {/* Mensagem para outros tipos de arquivo */}
          {attachment.mimetype !== 'application/pdf' && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Visualização não disponível para este tipo de arquivo. Use o botão "Download" para baixar o arquivo.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  });

  return (
    <>
      {!isLoading && !error && (
        <Container
          title={`${policy?.name} - Versão (${policy?.policyVersions[idVersion].version})`}
          breadcrumb={`Adequações / Políticas / ${policy?.name} / Versões`}
          linkPage={`/policies/show/${id}`}
          actionButton={
            policy?.policyVersions[idVersion]?.publishedDate && canExportPDF() ? (
              <ButtonForm 
                secondary 
                id="printPdf" 
                label="Exportar PDF" 
                onClick={() => handlePrintWithAttachment()}
              />
            ) : null
          }
        >
          <Box
            ref={componentRef}
            sx={{
              width: '100%',
              fontFamily:
                '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
            }}
            className="container"
          >
            <Row className="mb-1 pl-3">
              <Column xs={12}>
                <h3 className="m-0">{policy.name}</h3>
              </Column>
              <Column xs={12}>
                Versão: {getVersionPolicy(policy, idVersion)} | Classificação: {getClassificationPolicy(policy)}
              </Column>
              <Column xs={12}>
                Publicação: {policy?.policyVersions[idVersion]?.publishedDate ? formatDateToDateBRPolish(policy.policyVersions[idVersion].publishedDate) : 'Não publicado'}
              </Column>
              <Column xs={12}>Autor: {policy?.policyVersions[idVersion]?.author?.name || 'N/A'}</Column>
            </Row>
            {!policy?.policyVersions[idVersion]?.attachment && (
              <div>
                <h3
                  style={{
                    textAlign: 'center',
                    fontSize: '1.75rem',
                    fontWeight: '500',
                    marginTop: '1.5rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  {policy.name}
                </h3>
              </div>
            )}
            <div
              style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
              className="ql-snow ql-editor"
              dangerouslySetInnerHTML={{ __html: policy?.policyVersions[idVersion].policyText }}
            ></div>
            
            {/* Exibir anexo se existir */}
            {policy?.policyVersions[idVersion]?.attachment && (
              <AttachmentViewer attachment={policy.policyVersions[idVersion].attachment} />
            )}
            
            <TableContainer
              component={Paper}
              style={{
                marginBottom: '1rem',
              }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow key={'table-row-header'}>
                    <TableCell key={`thrc-stage`}>Estágio</TableCell>
                    <TableCell key={`thrc-status`}>Status</TableCell>
                    <TableCell key={`thrc-date`}>Data</TableCell>
                    <TableCell key={`thrc-sponsor`}>Responsável</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key={'table-row-header-review'}>
                    <TableCell key={`thrc-stage-1`}>Revisão</TableCell>
                    <TableCell key={`thrc-status-1`}>{getStatusReview(policy?.policyVersions[idVersion])}</TableCell>
                    <TableCell key={`thrc-date-1`}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion].reviewedDate)}
                    </TableCell>
                    <TableCell key={`thrc-sponsor-1`}>{policy?.policyVersions[idVersion]?.reviewer?.name || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow key={'table-row-header-approve'}>
                    <TableCell key={`thrc-stage-2`}>Aprovação</TableCell>
                    <TableCell key={`thrc-status-2`}>{getStatusApprove(policy?.policyVersions[idVersion])}</TableCell>
                    <TableCell key={`thrc-date-2`}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion].approvedDate)}
                    </TableCell>
                    <TableCell key={`thrc-sponsor-2`}>{policy?.policyVersions[idVersion]?.approver?.name || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow key={'table-row-header-publish'}>
                    <TableCell key={`thrc-stage-3`}>Publicação</TableCell>
                    <TableCell key={`thrc-status-3`}>{getStatusPublish(policy?.policyVersions[idVersion])}</TableCell>
                    <TableCell key={`thrc-date-3`}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion].publishedDate)}
                    </TableCell>
                    <TableCell key={`thrc-sponsor-3`}>{policy?.policyVersions[idVersion]?.author?.name || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Histórico de Comentários */}
            {canViewApprovalHistory() && (
            <Accordion sx={{ mb: 2 }} className="no-print">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="approval-history-content"
                id="approval-history-header"
                sx={{
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1976d2' }}>
                    <IndeterminateCheckBoxOutlinedIcon />
                    Histórico de Aprovação {policy?.policyVersions[idVersion]?.comments && policy.policyVersions[idVersion].comments.length > 0 && `(${policy.policyVersions[idVersion].comments.length})`}
                  </Typography>
                  {canAddComment() && (
                    <ButtonForm
                      secondary
                      label="Adicionar Comentário"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddComment();
                      }}
                      size="small"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                
                {comments && comments.length > 0 ? (
                  comments
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Ordenar por data (mais recente primeiro)
                    .map((comment, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        mb: 2, 
                        p: 2, 
                        backgroundColor: comment.type === 'rejection' ? '#ffebee' : '#f0f8ff',
                        borderRadius: 1,
                        borderLeft: `4px solid ${comment.type === 'rejection' ? '#d32f2f' : '#1976d2'}`
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: comment.type === 'rejection' ? '#d32f2f' : '#1976d2' }}>
                          {comment.type === 'rejection' ? '🚫 Recusado' : '💬 Comentário'} por {comment.authorName || 'Usuário'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTimeToDateTimeBR(comment.createdAt)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Função:</strong> {(() => {
                          // Determinar o papel correto do usuário baseado na política atual
                          if (policy && policy.policyVersions && policy.policyVersions[parseInt(idVersion)]) {
                            const currentVersion = policy.policyVersions[parseInt(idVersion)];
                            const commentAuthorId = comment.authorId;
                            
                            if (currentVersion.author && (currentVersion.author._id === commentAuthorId || currentVersion.author.id === commentAuthorId)) {
                              return 'Autor';
                            } else if (currentVersion.reviewer && (currentVersion.reviewer._id === commentAuthorId || currentVersion.reviewer.id === commentAuthorId)) {
                              return 'Revisor';
                            } else if (currentVersion.approver && (currentVersion.approver._id === commentAuthorId || currentVersion.approver.id === commentAuthorId)) {
                              return 'Aprovador';
                            }
                          }
                          return comment.authorRole || 'Usuário';
                        })()}
                      </Typography>
                      
                      <Typography variant="body1" sx={{ 
                        fontStyle: comment.type === 'rejection' ? 'normal' : 'italic',
                        backgroundColor: 'white',
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}>
                        "{comment.content}"
                      </Typography>
                      
                      {comment.type === 'rejection' && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ 
                            backgroundColor: '#d32f2f', 
                            color: 'white', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontWeight: 'bold'
                          }}>
                            DOCUMENTO RECUSADO
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Nenhum comentário ainda. Seja o primeiro a comentar!
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            )}
            
            {policy?.policyVersions[idVersion]?.publishedDate && policyAccepts?.total > 0 && (
              <div>
                <ButtonForm
                  label="Ver Aceites"
                  onClick={() => navigate(`/policies/show/${id}/version/${idVersion}/accepts`)}
                />
              </div>
            )}
            {policy?.policyVersions[idVersion]?.publishedDate && policyAccepts?.total === 0 && (
              <div>
                <ButtonForm label="Enviar Aceites" onClick={() => mutation.mutate()} />
              </div>
            )}
          </Box>
        </Container>
      )}
      {isLoading && <div>Carregando</div>}
      {error && <div>Erro</div>}
      
      {/* Componente invisível para impressão combinada */}
      {showCombinedContent && (
        <div style={{ display: 'none' }}>
          <div ref={combinedRef}>
            {/* Cabeçalho da política */}
            <div style={{ marginBottom: '40px' }}>
              <h1 style={{ textAlign: 'center', marginBottom: '30px', fontSize: '24px', fontWeight: 'bold' }}>
                {policy?.name}
              </h1>
              <div style={{ marginBottom: '15px', fontSize: '14px' }}>
                <strong>Versão:</strong> {policy?.policyVersions[idVersion]?.version} | <strong>Classificação:</strong> {policy?.classification}
              </div>
              <div style={{ marginBottom: '15px', fontSize: '14px' }}>
                <strong>Data de Publicação:</strong> {policy?.policyVersions[idVersion]?.publishedDate ? formatDateToDateBRPolish(policy.policyVersions[idVersion].publishedDate) : 'Não publicado'}
              </div>
              <div style={{ marginBottom: '30px', fontSize: '14px' }}>
                <strong>Autor:</strong> {policy?.policyVersions[idVersion]?.author?.name || 'N/A'}
              </div>
            </div>

            {/* Conteúdo da política */}
            <div style={{ lineHeight: '1.6', marginBottom: '40px' }}>
              <div dangerouslySetInnerHTML={{ __html: policy?.policyVersions[idVersion]?.policyText || '' }} />
            </div>

            {/* Tabela de Status */}
            <div style={{ marginBottom: '40px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', borderBottom: '2px solid #333', paddingBottom: '10px' }}>
                Status de Aprovação
              </h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      Estágio
                    </th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      Status
                    </th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      Data
                    </th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>
                      Responsável
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>Revisão</td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.reviewedDate ? '✅ Revisado' : '⏳ Pendente'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion]?.reviewedDate) || 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.reviewer?.name || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>Aprovação</td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.approvedDate ? '✅ Aprovado' : '⏳ Pendente'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion]?.approvedDate) || 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.approver?.name || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>Publicação</td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.publishedDate ? '✅ Publicado' : '⏳ Pendente'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion]?.publishedDate) || 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                      {policy?.policyVersions[idVersion]?.author?.name || 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="page-break" />
            
            {pdfPages.length > 0 && (
              <div style={{ marginTop: '20px', padding: '10px 0' }}>
                {pdfPages.map((src, i) => (
                  <div key={i} style={{ pageBreakBefore: i > 0 ? 'always' : 'auto', marginBottom: '10px' }}>
                    <img className="pdf-page-img" src={src} alt={`Página ${i + 1} do anexo`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Modal de Transcrição */}
      <Dialog 
        open={showTranscriptionModal} 
        onClose={() => setShowTranscriptionModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Transcrição do Documento - {policy?.policyVersions[idVersion]?.attachment?.originalName}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            O texto abaixo foi extraído do documento PDF. Você pode editá-lo conforme necessário:
          </Typography>
          <TextField
            multiline
            rows={20}
            fullWidth
            variant="outlined"
            value={transcribedText}
            onChange={(e) => setTranscribedText(e.target.value)}
            placeholder="Texto transcrito aparecerá aqui..."
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '14px',
                lineHeight: 1.6
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <ButtonForm 
            secondary 
            label="Cancelar" 
            onClick={() => setShowTranscriptionModal(false)} 
          />
          <ButtonForm 
            primary 
            label="Salvar Transcrição" 
            onClick={handleSaveTranscription}
            disabled={!transcribedText.trim()}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
